import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Box, Typography, Grid, Button } from '@mui/material';
import { RefreshCw, Home, BookOpen, Mail, Server, AlertTriangle, Hammer } from 'lucide-react';
import ErrorLayout from '../components/ErrorLayout';

const TERMINAL_COMMANDS = [
  { text: "$ npm run fix-server", delay: 800 },
  { text: "Checking database health...", delay: 1400 },
  { text: "CRITICAL: Connection pool exhausted. Retrying...", delay: 2000 },
  { text: "Restarting API Gateway service...", delay: 2800 },
  { text: "Validating cache cluster configs...", delay: 3500 },
  { text: "Success: Hot-swap active. Re-establishing bridges...", delay: 4200 },
  { text: "System healthy. Monitoring connection states...", delay: 4900 },
];

export default function Custom500() {
  const [terminalLines, setTerminalLines] = useState([]);
  const [cycleCount, setCycleCount] = useState(0);

  useEffect(() => {
    let timers = [];
    setTerminalLines([]);

    TERMINAL_COMMANDS.forEach((cmd, idx) => {
      const timer = setTimeout(() => {
        setTerminalLines((prev) => [...prev, cmd.text]);
      }, cmd.delay);
      timers.push(timer);
    });

    const restartTimer = setTimeout(() => {
      setCycleCount((prev) => prev + 1);
    }, 8500);
    timers.push(restartTimer);

    return () => timers.forEach(clearTimeout);
  }, [cycleCount]);

  const reloadPage = () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  return (
    <ErrorLayout errorCode="500">
      <Grid container spacing={6} alignItems="center" sx={{ px: { xs: 2, md: 4 }, width: '100%', m: 0 }}>
        
        {/* Left column: Text info and terminal */}
        <Grid item xs={12} lg={7} sx={{ display: 'flex', flexDirection: 'column', gap: 3, textAlign: 'left', p: '0 !important' }}>
          
          {/* Status Indicator */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.4 }}
            style={{ width: 'fit-content' }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                color: '#f87171',
                fontSize: '11px',
                fontFamily: 'monospace',
                fontWeight: 600,
                px: 2,
                py: 0.75,
                borderRadius: '9999px',
              }}
            >
              <AlertTriangle size={14} className="animate-pulse" />
              <span>HTTP STATUS_CODE: 500_INTERNAL_SERVER_ERROR</span>
            </Box>
          </motion.div>

          <Box>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 800,
                color: '#ffffff',
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                lineHeight: 1.2,
                mb: 2,
              }}
            >
              Something crashed... but our engineers are already debugging it.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#94a3b8',
                fontSize: { xs: '14px', sm: '16px' },
                maxWidth: '500px',
                lineHeight: 1.6,
              }}
            >
              Our servers encountered an unexpected issue. Don't worry—the stack trace has been piped to our on-call rotation. Please try again in a few moments.
            </Typography>
          </Box>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
            <Button
              onClick={reloadPage}
              startIcon={<RefreshCw size={16} />}
              sx={{
                bgcolor: '#0891b2',
                color: '#ffffff',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '13px',
                px: 2.5,
                py: 1.1,
                borderRadius: '10px',
                boxShadow: '0 4px 12px rgba(8, 145, 178, 0.25)',
                '&:hover': {
                  bgcolor: '#06b6d4',
                  boxShadow: '0 4px 16px rgba(8, 145, 178, 0.4)',
                }
              }}
            >
              Reload Page
            </Button>
            <Button
              href="/"
              startIcon={<Home size={16} />}
              sx={{
                border: '1px solid #1e293b',
                color: '#cbd5e1',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '13px',
                px: 2.5,
                py: 1,
                borderRadius: '10px',
                '&:hover': {
                  bgcolor: 'rgba(30, 41, 59, 0.4)',
                  borderColor: '#334155',
                }
              }}
            >
              Home
            </Button>
            <Button
              href="/blogs"
              startIcon={<BookOpen size={16} />}
              sx={{
                border: '1px solid #1e293b',
                color: '#cbd5e1',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '13px',
                px: 2.5,
                py: 1,
                borderRadius: '10px',
                '&:hover': {
                  bgcolor: 'rgba(30, 41, 59, 0.4)',
                  borderColor: '#334155',
                }
              }}
            >
              Latest Articles
            </Button>
            <Button
              href="/contact"
              startIcon={<Mail size={16} />}
              sx={{
                border: '1px solid #1e293b',
                color: '#cbd5e1',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '13px',
                px: 2.5,
                py: 1,
                borderRadius: '10px',
                '&:hover': {
                  bgcolor: 'rgba(30, 41, 59, 0.4)',
                  borderColor: '#334155',
                }
              }}
            >
              Contact
            </Button>
          </Box>

          {/* Animated loop terminal */}
          <Box
            sx={{
              width: '100%',
              maxWidth: '500px',
              border: '1px solid rgba(30, 41, 59, 0.8)',
              bgcolor: 'rgba(7, 9, 13, 0.95)',
              borderRadius: '12px',
              p: 2.5,
              fontFamily: 'monospace',
              fontSize: '11px',
              color: '#cbd5e1',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.6)',
              backdropFilter: 'blur(8px)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'between', borderBottom: '1px solid #0f172a', pb: 1, mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#475569' }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'rgba(239, 68, 68, 0.6)' }} />
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'rgba(234, 179, 8, 0.6)' }} />
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'rgba(34, 197, 94, 0.6)' }} />
                <Box component="span" sx={{ pl: 1, fontSize: '10px' }}>server_recovery.sh</Box>
              </Box>
              <Box 
                component="span" 
                sx={{ 
                  fontSize: '9px', 
                  color: 'rgba(6, 182, 212, 0.8)', 
                  bgcolor: 'rgba(6, 182, 212, 0.05)', 
                  px: 1.5, 
                  py: 0.25, 
                  borderRadius: '4px',
                  border: '1px solid rgba(6, 182, 212, 0.15)',
                  ml: 'auto'
                }}
              >
                LIVE_STDOUT
              </Box>
            </Box>

            <Box sx={{ minHeight: '140px', maxHeight: '180px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <AnimatePresence>
                {terminalLines.map((line, idx) => {
                  let color = '#cbd5e1';
                  if (line.startsWith('$')) color = '#94a3b8';
                  else if (line.includes('CRITICAL')) color = '#f87171';
                  else if (line.includes('Success') || line.includes('System')) color = '#22d3ee';

                  return (
                    <Box
                      key={`${idx}-${cycleCount}`}
                      component={motion.div}
                      initial={{ opacity: 0, x: -5 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      sx={{ color, lineHeight: 1.5 }}
                    >
                      {line}
                    </Box>
                  );
                })}
              </AnimatePresence>
              <Box 
                component="span" 
                sx={{ 
                  display: 'inline-block', 
                  width: '6px', 
                  height: '14px', 
                  bgcolor: '#06b6d4', 
                  animation: 'pulse 1.2s infinite',
                  ml: 0.5,
                  alignSelf: 'flex-start'
                }} 
              />
            </Box>
          </Box>
        </Grid>

        {/* Right column: Server rack */}
        <Grid item xs={12} lg={5} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px', position: 'relative', p: '0 !important' }}>
          <Box sx={{ position: 'relative', width: '100%', maxWidth: '340px', aspectRatio: '1/1', display: 'flex', alignItems: 'center', justifySelf: 'center', justifyContent: 'center' }}>
            
            {/* Ambient backdrop */}
            <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(239, 68, 68, 0.04)', borderRadius: '50%', filter: 'blur(80px)' }} />

            {/* AI Assistant Repair Node */}
            <Box
              component={motion.div}
              animate={{ y: [-12, 12, -12], rotate: [4, -4, 4] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
              sx={{
                position: 'absolute',
                top: 0,
                right: '16px',
                zIndex: 20,
                bgcolor: 'rgba(2, 6, 23, 0.9)',
                border: '1px solid #1e293b',
                borderRadius: '16px',
                p: 1.5,
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                backdropFilter: 'blur(8px)',
              }}
            >
              <Box sx={{ bgcolor: 'rgba(6, 182, 212, 0.1)', p: 1, borderRadius: '50%', color: '#22d3ee' }}>
                <Hammer size={18} className="animate-bounce" />
              </Box>
              <Box sx={{ textAlign: 'left', fontFamily: 'monospace' }}>
                <Typography sx={{ fontSize: '9px', color: '#64748b', textTransform: 'uppercase' }}>Automated Patch</Typography>
                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#22d3ee' }}>APPLYING_HOTFIX...</Typography>
              </Box>
            </Box>

            {/* Servers Rack */}
            <Box
              sx={{
                width: '224px',
                height: '288px',
                border: '1px solid rgba(30, 41, 59, 0.8)',
                background: 'linear-gradient(to bottom, rgba(15, 23, 42, 0.4), rgba(2, 6, 23, 0.9))',
                borderRadius: '24px',
                p: 2.5,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
                backdropFilter: 'blur(16px)',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {/* Unit 1 */}
              <Box sx={{ height: 64, border: '1px solid #1e293b', bgcolor: 'rgba(13, 18, 28, 0.85)', borderRadius: '12px', p: 1.5, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#94a3b8' }}>
                    <Server size={14} />
                    <Box component="span" sx={{ fontSize: '10px', fontFamily: 'monospace' }}>UNIT_01</Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10b981', animation: 'pulse 1.5s infinite' }} />
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10b981' }} />
                  </Box>
                </Box>
                <Box sx={{ width: '100%', bgcolor: '#020617', height: '4px', borderRadius: '2px', overflow: 'hidden' }}>
                  <Box sx={{ bgcolor: '#06b6d4', height: '100%', width: '80%', animation: 'pulse 2s infinite' }} />
                </Box>
              </Box>

              {/* Unit 2 (The broken one) */}
              <Box sx={{ height: 64, border: '1px solid rgba(239, 68, 68, 0.2)', bgcolor: 'rgba(127, 29, 29, 0.05)', borderRadius: '12px', p: 1.5, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#f87171' }}>
                    <Server size={14} className="animate-bounce" />
                    <Box component="span" sx={{ fontSize: '10px', fontFamily: 'monospace', fontWeight: 600 }}>UNIT_02</Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ef4444', animation: 'ping 1s infinite' }} />
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ef4444' }} />
                  </Box>
                </Box>
                <Box sx={{ width: '100%', bgcolor: '#020617', height: '4px', borderRadius: '2px', overflow: 'hidden' }}>
                  <Box sx={{ bgcolor: '#ef4444', height: '100%', width: '40%', animation: 'pulse 1.5s infinite' }} />
                </Box>
              </Box>

              {/* Unit 3 */}
              <Box sx={{ height: 64, border: '1px solid #1e293b', bgcolor: 'rgba(13, 18, 28, 0.85)', borderRadius: '12px', p: 1.5, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#94a3b8' }}>
                    <Server size={14} />
                    <Box component="span" sx={{ fontSize: '10px', fontFamily: 'monospace' }}>UNIT_03</Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10b981' }} />
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10b981', animation: 'pulse 1.5s infinite' }} />
                  </Box>
                </Box>
                <Box sx={{ width: '100%', bgcolor: '#020617', height: '4px', borderRadius: '2px', overflow: 'hidden' }}>
                  <Box sx={{ bgcolor: '#06b6d4', height: '100%', width: '95%' }} />
                </Box>
              </Box>

            </Box>

          </Box>
        </Grid>
      </Grid>
    </ErrorLayout>
  );
}
